module Client.Admin.UserGrid

open Client
open Browser
open System
open Elmish
open Shared
open Illy.SafeStack.Client 
open Illy.SafeStack.Client.jQueryUI
open Fable.React
open Fable.React.Props
open Shared.Admin.UserGrid
open SafeStack.Client

module SearchString = 
    let ofText = Text.toString >> Option.defaultValue "" >> SearchString

type Model = 
    {
        SortInfo: SortInfo
        IncludeUsers: IncludeUsersFilter
        GettingData: bool
        GettingFirstPage: bool
        SearchString: Text.Model
        TotalCount: int 
        CanLoadMoreData: bool
        LastPage: PageNo
        Data: UserDTO list 
        DebounceSearch: SearchString Debounce.Model
    }

type Msg =
    | ChangeSortInfo of SortInfo
    | ApiError of ApiError
    | GridPageLoaded of UserDTO list * totalCount: int
    | SearchStringMsg of Text.Msg
    | DebounceSearchMsg of SearchString Debounce.Msg
    | LoadMoreResults
    | RefreshGrid
    | UpdateFilters of IncludeUsersFilter
    | CloseOverlay

type Event =
    | ApiError of ApiError
    | CloseOverlay

module private Initialisations = 
    let initSearchString value =
        Text.CustomSettings.defaultOptions()
        |> Text.init "admin-user-grid-search" "Search String" (Some "Start typing to filter users...") 5000. false value

let getGridDataCmd includeUsers pageNo sortInfo searchStr =
    Api.secureCall<IUserGridApi,_,_,_> SecuredRoute.Admin (fun api -> api.getGridPage pageNo searchStr includeUsers) sortInfo GridPageLoaded Msg.ApiError

let init includeUsers = 
    {
        SortInfo = SortInfo.defaults
        IncludeUsers = includeUsers
        GettingData = true
        GettingFirstPage = true
        SearchString = Initialisations.initSearchString None
        TotalCount = 0
        Data = []
        CanLoadMoreData = false
        LastPage = PageNo 1
        DebounceSearch = Debounce.init (TimeSpan.FromMilliseconds 300.) SearchString.empty
    }, 
    getGridDataCmd includeUsers (PageNo 1) SortInfo.defaults SearchString.empty

let update msg model = 
    match msg with
    | Msg.CloseOverlay -> model, CloseOverlay |> SideEffect.Event
    | Msg.ApiError err -> model, err |> ApiError |> SideEffect.Event
    | GridPageLoaded (d, totalCount) -> 
        {model with 
            GettingFirstPage = false 
            GettingData = false
            Data = if model.GettingFirstPage then d else model.Data @ d 
            TotalCount = totalCount
            CanLoadMoreData = (PageNo.unwrap model.LastPage) * PAGE_SIZE <= totalCount
        }, SideEffect.none
    | SearchStringMsg cMsg ->
        let m, c = Text.update cMsg model.SearchString
        let hasSearchStrChanged = Text.toString m <> Text.toString model.SearchString
        {model with 
            SearchString = m
            GettingData = hasSearchStrChanged
            GettingFirstPage = hasSearchStrChanged
            LastPage = if hasSearchStrChanged then PageNo 1 else model.LastPage
        }, Cmd.batch [
            yield Cmd.map SearchStringMsg c
            if hasSearchStrChanged then yield Debounce.inputCmd (SearchString.ofText m) DebounceSearchMsg
        ] |> SideEffect.Cmd
    | DebounceSearchMsg dMsg -> 
        let m, c = 
            Debounce.updateWithCmd
                dMsg
                DebounceSearchMsg
                model.DebounceSearch
                (fun x -> 
                    { model with
                        DebounceSearch = x 
                    })
                (getGridDataCmd model.IncludeUsers (PageNo 1) model.SortInfo)
        m , SideEffect.Cmd c
    | RefreshGrid -> model, Cmd.ofMsg (UpdateFilters model.IncludeUsers) |> SideEffect.Cmd
    | UpdateFilters filters -> 
        {model with 
            IncludeUsers = filters
            GettingData = true
            GettingFirstPage = true
            LastPage = PageNo 1
        }, model.SearchString |> SearchString.ofText |> getGridDataCmd filters (PageNo 1)  model.SortInfo |> SideEffect.Cmd
    | ChangeSortInfo sInfo -> 
        {model with 
            SortInfo = sInfo
            GettingData = true
            GettingFirstPage = true
            LastPage = PageNo 1
        }, model.SearchString |> SearchString.ofText |> getGridDataCmd model.IncludeUsers (PageNo 1) sInfo |> SideEffect.Cmd
    | LoadMoreResults -> 
        if not model.CanLoadMoreData || model.GettingData then model, SideEffect.none
        else 
            let newPage = PageNo.increment model.LastPage
            {model with
                GettingData = true 
                LastPage = newPage
            }, model.SearchString |> SearchString.ofText |> getGridDataCmd model.IncludeUsers newPage  model.SortInfo |> SideEffect.Cmd

let callDebounce = debounce 500

let viewContent model dispatch gridColumns = 
    div [
        Key "user-search-container"
        ClassName "user-search-container"
    ] [
        yield Text.textInput false model.SearchString (SearchStringMsg >> dispatch)

        yield div [
            Key "user-search-results-container"
            Id "user-search-results-container"
            Class "user-search-results"
            Style [
                Height "300px"
                OverflowY OverflowOptions.Scroll
            ]
            OnScroll (fun _ -> 
                if not model.GettingData && model.CanLoadMoreData then
                    let parent = document.getElementById("user-search-results-container")
                    if (parent.scrollHeight - parent.scrollTop - parent.clientHeight) <= 1. then
                        callDebounce
                            (fun (changeEv,_) -> dispatch changeEv )
                            LoadMoreResults
                            ()
            )
        ] [
            yield jQueryUI.simpleTable "No users found. Please update your search criteria and try again."
                gridColumns model.GettingFirstPage model.Data
            if model.GettingData && not model.GettingFirstPage then 
                yield jQueryUI.spinner
        ]
    ]

let isLoadingMoreResults (m: Model) = m.GettingData && not m.GettingFirstPage

let viewModal overlayTitle model dispatch userGridColumns = 
    let title = 
        if isLoadingMoreResults model then "Loading more results..."
        else overlayTitle
    jQueryUI.modal title 50 Msg.CloseOverlay dispatch (Some 900) [
        viewContent model dispatch userGridColumns
    ]

let sortHeader loading (currentSort: SortInfo) dispatch displayName fieldToSelect = 
    span [ 
        yield Style [
            Cursor "pointer"
        ]
        if not loading then 
            yield OnClick 
                (fun _ -> 
                    if currentSort.Field = fieldToSelect then 
                        dispatch (ChangeSortInfo { Field = fieldToSelect; Direction = SortDirection.toggle currentSort.Direction})
                    else 
                        dispatch (ChangeSortInfo { Field = fieldToSelect; Direction = SortDirection.Asc})
                )
    ] [
        yield str displayName
        if currentSort.Field = fieldToSelect then 
            match currentSort.Direction with 
            | SortDirection.Asc -> 
                yield FontAwesome.solid "chevron-up" [ FontAwesome.Fixed ] []
            | SortDirection.Desc -> 
                yield FontAwesome.solid "chevron-down" [ FontAwesome.Fixed ] []
    ]
    |> Tooltip.create [
        str "Click to sort by "
        str displayName
    ]

module GridColumn =
    
    let nameWithTooltip loading currentSort dispatch propGetter =
        Some [
            sortHeader loading currentSort dispatch "Name" SortField.FullName
        ] ===> fun (x: UserDTO) -> [
            let content = 
                [
                    str x.Forename 
                    str " " 
                    str x.Surname 
                ]
            match propGetter x with
            | None -> yield span [] content
            | Some strValue ->
                yield
                    abbr [ Style [ Cursor "help" ] ] content
                    |> Tooltip.create [ str strValue ]
        ]
    let name loading currentSort dispatch =
        nameWithTooltip loading currentSort dispatch (fun _ -> None)

    let username loading currentSort dispatch =
        Some [
            sortHeader loading currentSort dispatch "Username" SortField.Username
        ] ===> fun (x: UserDTO) -> [ str x.Username ]
    let email loading currentSort dispatch =
        Some [
            sortHeader loading currentSort dispatch "Email" SortField.Email
        ] ===> fun (x: UserDTO) -> [ str x.Email ]


